import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { StorageKeys, AuthConfig } from '@/lib/config';
import { createLogger } from '@/lib/logger';
import { sleep } from '@/lib/utils';

const log = createLogger('api');

const SKIP_AUTH_PATHS = ['/auth/login', '/auth/refresh', '/health'];

interface RetryableConfig extends AxiosRequestConfig {
  _retryCount?: number;
  _refreshAttempted?: boolean;
}

let client: AxiosInstance | null = null;
let refreshInflight: Promise<void> | null = null;

function readApiUrl(): string | null {
  const raw = localStorage.getItem(StorageKeys.apiUrl);
  if (!raw) return null;
  return normaliseUrl(raw);
}

/**
 * The base URL actually used for requests. Falls back to the page origin so a
 * combined single-folder deployment (PWA served by the middleware) works with
 * zero configuration. An explicitly configured URL (ConnectionSetup) always
 * wins, which keeps separate-origin hosting possible.
 */
function effectiveBaseUrl(): string | null {
  const stored = readApiUrl();
  if (stored) return stored;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return normaliseUrl(window.location.origin);
  }
  return null;
}

function normaliseUrl(raw: string): string {
  let trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (!/^https?:\/\//i.test(trimmed)) {
    trimmed = `https://${trimmed}`;
  }
  trimmed = trimmed.replace(/\/+$/, '');
  return trimmed;
}

/**
 * Persist the API base URL. The next call to {@link getClient} will
 * pick it up.
 */
export function setApiUrl(url: string): void {
  const norm = normaliseUrl(url);
  localStorage.setItem(StorageKeys.apiUrl, norm);
  client = null; // force re-init with new base
}

export function getApiUrl(): string | null {
  return readApiUrl();
}

export function clearApiUrl(): void {
  localStorage.removeItem(StorageKeys.apiUrl);
  client = null;
}

export function hasApiUrl(): boolean {
  return Boolean(effectiveBaseUrl());
}

/**
 * Build (or reuse) the singleton axios client with our three interceptors:
 *   1. Auth (token attach + proactive refresh)
 *   2. Refresh-on-401 + retry once
 *   3. Network/timeout retry with backoff
 */
export function getClient(): AxiosInstance {
  if (client) return client;

  const baseUrl = effectiveBaseUrl();
  const instance = axios.create({
    baseURL: baseUrl ? `${baseUrl}/api` : undefined,
    timeout: 30_000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use(async (config) => {
    const path = (config.url ?? '').replace(/^\/+/, '');
    const skipAuth = SKIP_AUTH_PATHS.some((p) => path.startsWith(p.replace(/^\//, '')));
    if (!skipAuth) {
      await maybeProactiveRefresh();
      const token = localStorage.getItem(StorageKeys.accessToken);
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      if (!config) throw error;

      // 401 -> attempt one refresh
      if (error.response?.status === 401 && !config._refreshAttempted) {
        config._refreshAttempted = true;
        try {
          await refreshTokens();
          const token = localStorage.getItem(StorageKeys.accessToken);
          if (token) {
            config.headers = config.headers ?? {};
            (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
          }
          return instance(config);
        } catch (err) {
          // Only nuke the session when the server *definitively* rejected the
          // refresh token (truly expired/invalid). A network blip / timeout
          // while the driver is travelling must NOT log them out -- keep the
          // tokens so the next request can retry once connectivity returns.
          if (isAuthRejection(err)) {
            log.warn('refresh rejected by server; clearing tokens', err);
            clearTokens();
          } else {
            log.warn('refresh failed transiently; keeping session', err);
          }
          throw error;
        }
      }

      // Network/timeout retry
      const isRetryable =
        !error.response &&
        (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.code === 'ETIMEDOUT');
      if (isRetryable) {
        config._retryCount = (config._retryCount ?? 0) + 1;
        if (config._retryCount <= 2) {
          await sleep(500 * 2 ** (config._retryCount - 1));
          return instance(config);
        }
      }
      throw error;
    },
  );

  client = instance;
  return instance;
}

/**
 * Persist auth tokens issued by /auth/login or /auth/refresh.
 */
export function persistTokens(payload: {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}): void {
  localStorage.setItem(StorageKeys.accessToken, payload.accessToken);
  localStorage.setItem(StorageKeys.refreshToken, payload.refreshToken);
  localStorage.setItem(StorageKeys.tokenExpiresAt, payload.expiresAt);
}

export function clearTokens(): void {
  localStorage.removeItem(StorageKeys.accessToken);
  localStorage.removeItem(StorageKeys.refreshToken);
  localStorage.removeItem(StorageKeys.tokenExpiresAt);
}

/**
 * True only when the refresh request came back with an HTTP auth error
 * (401/403/400) -- i.e. the refresh token is genuinely invalid/expired.
 * Network errors, timeouts and 5xx are treated as transient.
 */
function isAuthRejection(err: unknown): boolean {
  const status = (err as AxiosError)?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

async function maybeProactiveRefresh(): Promise<void> {
  const expiresAt = localStorage.getItem(StorageKeys.tokenExpiresAt);
  if (!expiresAt) return;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms < AuthConfig.tokenRefreshBufferMs) {
    try {
      await refreshTokens();
    } catch (err) {
      log.warn('proactive refresh failed', err);
    }
  }
}

async function refreshTokens(): Promise<void> {
  if (refreshInflight) return refreshInflight;
  const refreshToken = localStorage.getItem(StorageKeys.refreshToken);
  if (!refreshToken) throw new Error('no refresh token');

  refreshInflight = (async () => {
    const baseUrl = effectiveBaseUrl();
    if (!baseUrl) throw new Error('no API base URL configured');
    const resp = await axios.post(
      `${baseUrl}/api/auth/refresh`,
      { refreshToken },
      { timeout: 15_000, headers: { 'Content-Type': 'application/json' } },
    );
    const data = resp.data as {
      accessToken: string;
      refreshToken: string;
      expiresAt: string;
    };
    persistTokens(data);
  })()
    .finally(() => {
      refreshInflight = null;
    });

  return refreshInflight;
}

/**
 * Test-only helper: blow away the singleton between cases.
 */
export function __resetClientForTests(): void {
  client = null;
  refreshInflight = null;
}
